import { LIBRARY_DATE_PICKER } from '@app/constants/datePicker.const';
import { DatePicker, DatePickerProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import moment, { Moment } from 'moment';
import { FC, useMemo } from 'react';

// ===== Adapter Pattern =====
// Mỗi library cần implement 2 hàm:
//   - toDayjs: convert từ library value → Dayjs (để truyền vào Antd DatePicker)
//   - fromDayjs: convert từ Dayjs → library value (để trả về cho consumer)
// Khi thêm library mới, chỉ cần:
//   1. Thêm key vào LIBRARY_DATE_PICKER constant
//   2. Thêm 1 entry vào dateAdapters map bên dưới

type DateValue = Dayjs | Moment;

interface DateAdapter {
    toDayjs: (val: DateValue) => Dayjs;
    fromDayjs: (val: Dayjs) => DateValue;
}

const dateAdapters: Record<string, DateAdapter> = {
    [LIBRARY_DATE_PICKER.MOMENT]: {
        toDayjs: (val) => dayjs((val as Moment).toISOString()),
        fromDayjs: (val) => moment(val.toISOString()),
    },
    [LIBRARY_DATE_PICKER.DAYJS]: {
        toDayjs: (val) => val as Dayjs,
        fromDayjs: (val) => val,
    },
};

// ===== Component =====

interface IScDatePicker extends Omit<
    DatePickerProps,
    | 'onChange'
    | 'defaultValue'
    | 'format'
    | 'value'
    | 'disabledDate'
    | 'onCalendarChange'
> {
    onChange?: (value: DateValue, dataString?: string) => void;
    library?: string;
    defaultValue?: DateValue;
    value?: DateValue;
    format?: string | ((value: DateValue) => string);
    disabledDate?: (value: DateValue) => boolean;
    onCalendarChange?: (
        value: DateValue,
        dataString?: string,
        info?: unknown,
    ) => void;
}

const ScDatePicker: FC<IScDatePicker> = ({
    className = '',
    onChange,
    library = LIBRARY_DATE_PICKER.MOMENT,
    defaultValue,
    value,
    format,
    disabledDate,
    onCalendarChange,
    ...restProps
}) => {
    const adapter =
        dateAdapters[library] ?? dateAdapters[LIBRARY_DATE_PICKER.DAYJS];

    const handleChange = (dayjsValue: Dayjs, dataString: string) => {
        onChange?.(adapter.fromDayjs(dayjsValue), dataString);
    };

    const normalizedDefaultValue = useMemo(() => {
        if (!defaultValue) return undefined;
        return adapter.toDayjs(defaultValue);
    }, [defaultValue, adapter]);

    const normalizedValue = useMemo(() => {
        if (!value) return undefined;
        return adapter.toDayjs(value);
    }, [value, adapter]);

    const formatValue =
        typeof format === 'function'
            ? (dayjsValue: Dayjs) => format(adapter.fromDayjs(dayjsValue))
            : format;

    const handleDisabledDate = disabledDate
        ? (dayjsValue: Dayjs) => disabledDate(adapter.fromDayjs(dayjsValue))
        : undefined;

    const handleCalendarChange = onCalendarChange
        ? // eslint-disable-next-line @typescript-eslint/no-unused-vars
          (dayjsValue: Dayjs, dataString: string, info: unknown) => {
              onCalendarChange(adapter.fromDayjs(dayjsValue), dataString, info);
          }
        : undefined;

    return (
        <DatePicker
            {...restProps}
            className={`sc-antd-datepicker ${className}`}
            allowClear={false}
            onChange={handleChange}
            defaultValue={normalizedDefaultValue}
            value={normalizedValue}
            format={formatValue}
            disabledDate={handleDisabledDate}
            getPopupContainer={(trigger) =>
                trigger.parentElement || document.body
            }
            onCalendarChange={handleCalendarChange}
        />
    );
};

export default ScDatePicker;
